/**
 * Block 8.2 / 8.3 (2026-05-26): basic list pages для ФОП документів.
 *
 * Editor pages — TECHDEBT (поки тільки list view).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  singleTax,
  fopSelfEsv,
  fopSelfWz,
  type FopSelfPayment,
  type SingleTaxCalculation,
} from '@/api/single_tax';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function SingleTaxCalculationsPage() {
  const list = useQuery({
    queryKey: ['single-tax'],
    queryFn: () => singleTax.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<SingleTaxCalculation>[]>(
    () => [
      { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 110,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'tax_group', header: 'Група', width: 80, align: 'center',
        sortValue: (r) => r.tax_group, cell: (r) => r.tax_group,
      },
      {
        key: 'period_kind', header: 'Період', width: 110,
        sortValue: (r) => r.period_kind ?? '', cell: (r) => r.period_kind,
      },
      {
        key: 'period_year', header: 'Рік', width: 80, align: 'right',
        sortValue: (r) => r.period_year, cell: (r) => r.period_year,
      },
      {
        key: 'period_month', header: 'Міс', width: 70, align: 'right',
        sortValue: (r) => r.period_month ?? 0, cell: (r) => r.period_month,
      },
      {
        key: 'period_quarter', header: 'Кв', width: 60, align: 'right',
        sortValue: (r) => r.period_quarter ?? 0, cell: (r) => r.period_quarter,
      },
      {
        key: 'tax_amount', header: 'Сума ЄП', width: 130, align: 'right',
        sortValue: (r) => Number(r.tax_amount), cell: (r) => fmt2(r.tax_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ так' : 'ні',
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Єдиний податок ФОП</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<SingleTaxCalculation>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function FopSelfEsvPaymentsPage() {
  const list = useQuery({
    queryKey: ['fop-self-esv'],
    queryFn: () => fopSelfEsv.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<FopSelfPayment & { esv_amount: string }>[]>(
    () => [
      { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 110,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'period_year', header: 'Рік', width: 80, align: 'right',
        sortValue: (r) => r.period_year, cell: (r) => r.period_year,
      },
      {
        key: 'period_month', header: 'Міс', width: 70, align: 'right',
        sortValue: (r) => r.period_month, cell: (r) => r.period_month,
      },
      {
        key: 'esv_amount', header: 'Сума ЄСВ', width: 130, align: 'right',
        sortValue: (r) => Number(r.esv_amount), cell: (r) => fmt2(r.esv_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ так' : 'ні',
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>ФОП self ЄСВ</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<FopSelfPayment & { esv_amount: string }>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function FopSelfWzPaymentsPage() {
  const list = useQuery({
    queryKey: ['fop-self-wz'],
    queryFn: () => fopSelfWz.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<FopSelfPayment & { wz_amount: string }>[]>(
    () => [
      { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 110,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'period_year', header: 'Рік', width: 80, align: 'right',
        sortValue: (r) => r.period_year, cell: (r) => r.period_year,
      },
      {
        key: 'period_month', header: 'Міс', width: 70, align: 'right',
        sortValue: (r) => r.period_month, cell: (r) => r.period_month,
      },
      {
        key: 'wz_amount', header: 'Сума ВЗ', width: 130, align: 'right',
        sortValue: (r) => Number(r.wz_amount), cell: (r) => fmt2(r.wz_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ так' : 'ні',
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>ФОП self ВЗ</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<FopSelfPayment & { wz_amount: string }>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
